import {
    Component,
    forwardRef,
    input,
    InputSignal,
    ViewChild,
    effect,
    CUSTOM_ELEMENTS_SCHEMA,
    signal,
    WritableSignal,
    ElementRef,
    HostListener,
    computed,
    Signal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { TextValueAccessor } from '@shui/angular';

export interface ShSelectOption {
    value: string | number;
    label: string;
    disabled?: boolean;
    active?: boolean;
}

/**
 * Wrapper component for sh-dropdown that properly handles value accessor issues.
 *
 * Problems solved:
 * - Automatically adds display="label" to show labels instead of values
 * - Handles [active] binding for sh-menu-item to show selection state
 * - Converts comma-separated string to array for multi-select mode
 */
@Component({
    selector: 'sh-select',
    templateUrl: './sh-select.component.html',
    standalone: true,
    imports: [TranslatePipe, TextValueAccessor],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ShSelectComponent),
            multi: true
        }
    ],
    host: { class: 'block' },
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ShSelectComponent implements ControlValueAccessor {
    @ViewChild('dropdown', { static: false }) public dropdownElement!: ElementRef;

    // Inputs
    public label: InputSignal<string> = input<string>('');
    public disabled: InputSignal<boolean> = input<boolean>(false);
    public multiSelect: InputSignal<boolean> = input<boolean>(false);
    public condensed: InputSignal<boolean> = input<boolean>(false);
    public mandatory: InputSignal<boolean> = input<boolean>(false);
    public error: InputSignal<boolean> = input<boolean>(false);
    public errorMessage: InputSignal<string> = input<string>('');
    public options: InputSignal<ShSelectOption[]> = input<ShSelectOption[]>([]);
    public class: InputSignal<string> = input<string>('');
    public hint: InputSignal<string | undefined> = input<string | undefined>();
    public loading: InputSignal<boolean> = input<boolean>(false);
    public loadingLabel: InputSignal<string> = input<string>('common.general.loading');
    public searchable: InputSignal<boolean> = input<boolean>(false);
    public optionTestId: InputSignal<string | undefined> = input<string | undefined>(undefined);
    public noBorder: InputSignal<boolean> = input<boolean>(false);

    // Internal state
    protected currentValue: string | string[] | null = null;
    protected isDisabled: WritableSignal<boolean> = signal(false);
    protected dropdownKey: WritableSignal<number> = signal(0);
    protected readonly searchQuery: WritableSignal<string> = signal('');

    /**
     * In multi-select + searchable mode we render our own search field above the dropdown
     * and filter `options()` ourselves. The native `editable` filter on `sh-dropdown` is
     * tied to the dropdown's displayed value, which in multi-select mode is the joined
     * labels of the selected items — that makes the dropdown hide all unrelated options
     * the moment something is picked. Driving the filter from a dedicated search query
     * signal avoids that interference.
     */
    protected readonly useExternalSearch: Signal<boolean> = computed(() => this.multiSelect() && this.searchable());
    protected readonly visibleOptions: Signal<ShSelectOption[]> = computed(() => {
        const all = this.options();
        if (!this.useExternalSearch()) {
            return all;
        }
        const q = this.searchQuery().trim().toLowerCase();
        if (!q) {
            return all;
        }
        return all.filter((opt) => opt.label.toLowerCase().includes(q) || String(opt.value).toLowerCase().includes(q));
    });

    private onChange: (value: string | string[] | null) => void = () => {};

    private onTouched: () => void = () => {};

    constructor() {
        // Update active state when value changes
        effect(() => {
            this.updateActiveStates();
        });
    }

    // ControlValueAccessor implementation
    public writeValue(value: string | string[] | null): void {
        const normalized = this.normalizeValue(value);
        const wasPopulated = !this.isEmptyValue(this.currentValue);
        this.currentValue = normalized;
        if (wasPopulated && this.isEmptyValue(normalized)) {
            this.dropdownKey.update((k) => k + 1);
        }
        this.updateActiveStates();
    }

    public registerOnChange(fn: (value: string | string[] | null) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
    }

    /**
     * Handle click on menu item
     */
    protected handleMenuItemClick(value: string | number): void {
        const strValue = String(value);

        if (this.multiSelect()) {
            // Multi-select: toggle value in array
            const currentArray = Array.isArray(this.currentValue)
                ? this.currentValue
                : this.currentValue
                  ? String(this.currentValue)
                        .split(',')
                        .map((v) => v.trim())
                        .filter((v) => v)
                  : [];

            const index = currentArray.indexOf(strValue);
            if (index > -1) {
                // Remove if already selected
                currentArray.splice(index, 1);
            } else {
                // Add if not selected
                currentArray.push(strValue);
            }

            this.currentValue = currentArray;
            this.onChange(currentArray);
        } else {
            // Single select: set value directly
            this.currentValue = strValue;
            this.onChange(strValue);
        }

        this.onTouched();
    }

    /**
     * Handle the clear (X) icon click emitted by sh-dropdown.
     * The web component resets its own display; propagate the cleared value to the form.
     */
    protected handleClear(): void {
        this.currentValue = this.multiSelect() ? [] : null;
        this.onChange(this.currentValue);
        this.onTouched();
    }

    @HostListener('focusout')
    protected onHostFocusOut(): void {
        this.onTouched();
    }

    protected onSearchInput(event: Event): void {
        const input = event.target as HTMLInputElement | null;
        this.searchQuery.set(input?.value ?? '');
    }

    protected clearSearch(): void {
        this.searchQuery.set('');
    }

    /**
     * Determine whether a (normalized) value represents an empty selection.
     * An empty array is truthy, so it must be checked explicitly.
     */
    private isEmptyValue(value: string | string[] | null): boolean {
        if (value === null || value === undefined || value === '') {
            return true;
        }
        return Array.isArray(value) && value.length === 0;
    }

    /**
     * Normalize value - convert comma-separated string to array for multi-select
     */
    private normalizeValue(value: string | string[] | null): string | string[] | null {
        if (!value) {
            return this.multiSelect() ? [] : null;
        }

        if (this.multiSelect()) {
            if (typeof value === 'string') {
                // Convert comma-separated string to array
                return value
                    .split(',')
                    .map((v) => v.trim())
                    .filter((v) => v);
            }
            return Array.isArray(value) ? value : [value];
        }

        // Single select - always return string
        return Array.isArray(value) ? value[0] || null : value;
    }

    /**
     * Check if an option is currently active/selected
     */
    protected isOptionActive(optionValue: string | number): boolean {
        if (!this.currentValue) {
            return false;
        }

        const strValue = String(optionValue);

        if (this.multiSelect()) {
            const valueArray = Array.isArray(this.currentValue)
                ? this.currentValue
                : String(this.currentValue)
                      .split(',')
                      .map((v) => v.trim());
            return valueArray.includes(strValue);
        }

        return String(this.currentValue) === strValue;
    }

    /**
     * Update active states for all options
     */
    private updateActiveStates(): void {
        // This ensures options are re-evaluated when value changes
        // The template will use isOptionActive() to determine [active] state
    }

    /**
     * Get the display value for the dropdown
     */
    protected getDisplayValue(): string {
        if (!this.currentValue) {
            return '';
        }

        const values = Array.isArray(this.currentValue) ? this.currentValue : [this.currentValue];
        const labels = values
            .map((v) => {
                const option = this.options().find((opt) => String(opt.value) === String(v));
                return option?.label || v;
            })
            .filter(Boolean);

        return labels.join(', ');
    }
}
